"use client";

import { useEffect } from "react";
import {
  IconGauge,
  IconList,
  IconShare,
  IconUsersGroup,
  IconBell,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Code,
  Container,
  Flex,
  Group,
  ScrollArea,
  Badge,
  Burger,
  Drawer,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "react-router-dom";
import { LinksGroup } from "../NavbarLinksGroup/NavbarLinksGroup";
import { UserButton } from "../UserButton/UserButton";
import { Logo } from "./Logo";
import Upload from "@/features/Upload";
import classes from "./DashboardLayout.module.css";
import { FeedbackButton } from "../FeedbackButton/FeedbackButton";
import { useGetNotificationsQuery } from "@/features/Notification/Notification.api";
import { notifications } from "@mantine/notifications";
import { useDispatch } from "react-redux";
import { contentApi } from "@/features/Content/Content.api";

import { useUser } from "@clerk/clerk-react";
import { useGetLimitsQuery } from "@/features/Setting/Setting.api";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const sidebarLinks = [
  { label: "Dashboard", icon: IconGauge, link: "/dashboard" },
  {
    label: "Contents",
    icon: IconList,
    link: "/contents",
  },
  { label: "Members", icon: IconUsersGroup, link: "/members" },
  { label: "Workspaces", icon: IconShare, link: "/workspaces" },
  { label: "Inbox", icon: IconBell, link: "/inbox" },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const dispatch = useDispatch();
  const { user } = useUser();
  useGetLimitsQuery(user?.id || "", { skip: !user?.id });
  const { data: notificationsList = [], refetch } = useGetNotificationsQuery();
  const [opened, { toggle, close }] = useDisclosure(false);
  const { pathname } = useLocation();

  const unreadCount = notificationsList.filter((n) => !n.read).length;

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 1000;
    let destroyed = false;

    const connectSSE = async () => {
      if (destroyed) return;

      try {
        const token = await (window as any).Clerk?.session?.getToken();
        if (!token || destroyed) return;

        const sseUrl = `${import.meta.env.VITE_API_URL}/v1/notifications/stream?token=${token}`;
        eventSource = new EventSource(sseUrl);

        eventSource.addEventListener("ping", () => {
          // Connection confirmed — reset backoff
          retryDelay = 1000;
        });

        eventSource.addEventListener("notification", (event) => {
          try {
            const payload = JSON.parse(event.data);
            notifications.show({
              title: payload.title || "New Notification",
              message: payload.message || "",
              color:
                payload.type === "ERROR"
                  ? "red"
                  : payload.type === "SUCCESS"
                    ? "green"
                    : "blue",
            });
            refetch();
            dispatch(contentApi.util.invalidateTags(["Content"]));
          } catch (err) {
            console.error("Failed to parse SSE data:", err);
          }
        });

        eventSource.onerror = () => {
          eventSource?.close();
          eventSource = null;
          if (!destroyed) {
            reconnectTimer = setTimeout(() => {
              retryDelay = Math.min(retryDelay * 2, 30000);
              connectSSE();
            }, retryDelay);
          }
        };
      } catch (err) {
        console.error("SSE connect failed:", err);
        if (!destroyed) {
          reconnectTimer = setTimeout(() => {
            retryDelay = Math.min(retryDelay * 2, 30000);
            connectSSE();
          }, retryDelay);
        }
      }
    };

    connectSSE();

    return () => {
      destroyed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSource) eventSource.close();
    };
  }, [refetch]);

  const links = sidebarLinks.map((item) => {
    if (item.label === "Inbox") {
      return (
        <LinksGroup
          {...item}
          key={item.label}
          badge={
            unreadCount > 0 ? (
              <Badge color="red" size="sm" circle>
                {unreadCount}
              </Badge>
            ) : null
          }
        />
      );
    }
    return <LinksGroup {...item} key={item.label} />;
  });

  return (
    <Flex>
      {/* Mobile Top Header */}
      <div className={classes.mobileHeader}>
        <Group gap="xs">
          <Burger opened={opened} onClick={toggle} size="sm" />
          <Logo style={{ width: 100 }} />
        </Group>
      </div>

      {/* Mobile Side Drawer */}
      <Drawer
        opened={opened}
        onClose={close}
        size="300px"
        padding={0}
        withCloseButton={false}
      >
        <div className={classes.drawerContent}>
          <div className={classes.header}>
            <Group justify="space-between">
              <Logo />
              <Burger opened={opened} onClick={close} size="sm" />
            </Group>
          </div>

          <Upload />

          <ScrollArea className={classes.links}>
            <div className={classes.linksInner}>{links}</div>
          </ScrollArea>

          <FeedbackButton />

          <div className={classes.footer}>
            <UserButton />
          </div>
        </div>
      </Drawer>

      {/* Desktop Navigation Sidebar */}
      <nav className={classes.navbar}>
        <div className={classes.header}>
          <Logo />
        </div>

        <Upload />

        <ScrollArea className={classes.links}>
          <div className={classes.linksInner}>{links}</div>
        </ScrollArea>

        <FeedbackButton />

        <div className={classes.footer}>
          <UserButton />
        </div>
      </nav>

      {/* Main Content Area */}
      <Box className={classes.content} p="xl">
        {children}
      </Box>
    </Flex>
  );
}
